package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import kotlinx.browser.document
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.textDirection
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** Properties are the always-available, accessible caption path, including narrow screens. */
@Composable
internal fun TextProperties(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    val layer = state.selected.singleOrNull() as? dev.lingvo.shared.screenshots.doc.TextLayer
    val caption = state.caption
    if (layer == null && caption == null) return
    StudioDisclosure(t("studio.editor.text_properties.text", "Text")) {
        if (layer != null) TextStyleProperties(state, layer, dispatch)
        if (caption == null) return@StudioDisclosure
        CaptionConsequence(caption)
        Div(attrs = { style { property("font-family", "monospace") } }) { Text(caption.key) }
        LBadge(when (caption.state) {
            TranslationState.APPROVED -> t("studio.editor.text_properties.approved", "Approved")
            TranslationState.TRANSLATED -> t("studio.editor.text_properties.unapproved", "Unapproved")
            TranslationState.EMPTY -> t("studio.editor.text_properties.missing_caption", "Missing caption")
        })
        // lang and dir inherit onto LTextArea's DOM textarea from this wrapper.
        Div(attrs = {
            id("studio-caption-properties"); attr("lang", caption.locale); attr("dir", textDirection(caption.locale).html)
        }) {
            LTextArea(caption.draft, { dispatch(StudioIntent.CaptionInput(it)) }, label = t("studio.editor.text_properties.caption", "Caption"),
                enabled = caption.editable && state.editable, error = caption.error)
        }
        SideEffect {
            document.getElementById("studio-caption-properties")?.querySelector("textarea")?.apply {
                setAttribute("lang", caption.locale); setAttribute("dir", textDirection(caption.locale).html)
            }
        }
        if (captionPending(caption) && !caption.saving) StudioButton(t("studio.editor.text_properties.cancel_caption_changes", "Cancel caption changes")) {
            dispatch(StudioIntent.CaptionCancel)
        }
        FitMeter(caption)
        StudioButton(t("studio.editor.text_properties.save_caption", "Save caption"), state.editable && caption.editable && caption.draft != caption.value) {
            dispatch(StudioIntent.CaptionSave)
        }
    }
}

/** Consequences precede both editable fields in DOM and reading order, before any keystroke. */
@Composable
internal fun CaptionConsequence(caption: CaptionSnapshot) {
    P(attrs = { attr("data-caption-consequence", "") }) {
        Text(when {
            !caption.canWrite -> t("studio.editor.text_properties.this_caption_is_read_only", "This caption is read-only.")
            caption.sourceView == null -> t("studio.editor.text_properties.reload_this_caption_before_editing_to", "Reload this caption before editing to verify its source revision.")
            caption.locale == caption.sourceLocale -> tPlural("studio.caption_source_consequence", caption.affectedTranslations,
                "This is the source text. Changing it marks {count} translation for review.",
                "This is the source text. Changing it marks {count} translations for review.")
            caption.state == TranslationState.APPROVED -> t("studio.editor.text_properties.editing_makes_this_caption_unapproved_until", "Editing makes this caption unapproved until someone reviews it.")
            else -> t("studio.editor.text_properties.editing_this_caption_changes_the_translated", "Editing this caption changes the translated string.")
        })
    }
}
